import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ClipboardText, CheckCircle, Warning, WarningCircle } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* =========================================================================
 *  ProxyAuditPanel — every assessor-proxy formulation, in one feed.
 *
 *  Shows admin → client → workflow → status mapping for the on-behalf-of-
 *  client runs that the assessor has triggered. Essential for billing,
 *  accountability, and quality oversight.
 * ======================================================================= */

const STATUS = {
  acceptable:     { cls: 'bg-[#DEF7EC] text-[#03543F] border-[#31C48D]', Icon: CheckCircle, label: 'OK' },
  needs_review:   { cls: 'bg-[#FEF3C7] text-[#723B13] border-[#F3CC64]', Icon: Warning, label: 'REVIEW' },
  not_acceptable: { cls: 'bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]', Icon: WarningCircle, label: 'BLOCKED' },
};

export default function ProxyAuditPanel() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/admin/proxy-audit?limit=50`, { withCredentials: true });
        setEvents(r.data.events || []);
      } catch (e) { /* keep */ }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="card-flat bg-white" data-testid="proxy-audit-panel">
      <div className="p-4 border-b border-gray-200 flex items-center gap-2">
        <ClipboardText size={18} className="text-[#FF4E6A]" weight="duotone" />
        <h2 className="text-sm font-semibold text-[#111827]">Assessor proxy audit</h2>
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] bg-[#F3F4F6] px-2 py-0.5 rounded ml-auto tabular-nums">
          {events.length} run{events.length === 1 ? '' : 's'}
        </span>
      </div>
      {loading ? (
        <p className="text-sm text-[#4B5563] py-8 text-center">Loading…</p>
      ) : events.length === 0 ? (
        <p className="text-sm text-[#4B5563] py-8 text-center">No assessor-proxy runs yet. Use the <em>Run Assessment</em> panel above and pick a client to attribute a run.</p>
      ) : (
        <div className="divide-y divide-gray-100 max-h-[420px] overflow-y-auto">
          {events.map(e => {
            const m = STATUS[e.overall_status] || STATUS.needs_review;
            return (
              <Link key={e.id} to={`/assessment/${e.id}`} className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-gray-50" data-testid={`proxy-audit-row-${e.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-7 h-7 rounded flex items-center justify-center border ${m.cls}`}>
                    <m.Icon size={13} weight="fill" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-semibold text-[#111827] truncate">{e.product_name || 'Untitled'}</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-gray-100 text-[#4B5563] rounded">{e.assessment_mode}</span>
                      {e.signed && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-[#DEF7EC] text-[#03543F] border border-[#31C48D] rounded">SIGNED</span>}
                      {e.flagged && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-[#FDE8E8] text-[#9B1C1C] border border-[#F8B4B4] rounded">FLAG</span>}
                    </div>
                    <p className="text-[11px] text-[#4B5563] truncate">
                      <strong>{e.client?.name || e.client?.email || '—'}</strong>
                      {e.client?.company && <> · {e.client.company}</>}
                      <span className="mx-1.5 text-[#9CA3AF]">·</span>
                      run by {e.assessor?.name || e.assessor?.email || '—'}
                    </p>
                  </div>
                </div>
                <span className="text-[10px] font-mono tabular-nums text-[#4B5563] flex-shrink-0">{new Date(e.created_at).toLocaleString()}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
